import config from '@/config';
import axiosInstance from '@/school-management/utils/api';
import { GroupsReportResponse, GroupsReportQueryParams } from '@/school-management/types/response';
import { ClassLevelControlSettings } from '@/types/register';
import { AxiosResponse } from 'axios';

const classesReportInFlight = new Map<string, Promise<GroupsReportResponse>>();

export interface ClassService {
  getAllClasses: (
    organizationId: string,
    queryParams?: GroupsReportQueryParams
  ) => Promise<GroupsReportResponse>;
  createClass: (
    organizationId: string,
    payload: {
      listOfTeachers: string[];
      groupName: string;
      gradeId: string;
    } & Partial<ClassLevelControlSettings>
  ) => Promise<AxiosResponse<any>>;
  getClassById: (schoolId: string, classId: string) => Promise<AxiosResponse<any>>;
  updateClass: (
    schoolId: string,
    classId: string,
    payload: {
      groupName: string;
      gradeId: string;
      teacherIds: string[];
      /** Version last seen by the client - see Class.lastUpdateAt. */
      expectedLastUpdateAt?: string;
    } & Partial<ClassLevelControlSettings>
  ) => Promise<AxiosResponse<any>>;
  deleteClass: (schoolId: string, classId: string) => Promise<AxiosResponse<any>>;
}

export const classService: ClassService = {
  async getAllClasses(
    organizationId: string,
    queryParams?: GroupsReportQueryParams
  ): Promise<GroupsReportResponse> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/groups/report/search`;

    const body: Record<string, any> = {
      accountId: organizationId,
      page: queryParams?.page ?? 1,
      pageSize: queryParams?.pageSize ?? config.groupsPageSize,
    };

    // Sorting
    if (queryParams?.sortBy) body.sortBy = queryParams.sortBy;
    if (queryParams?.sortDirection) body.sortDirection = queryParams.sortDirection;

    if (queryParams?.search && queryParams.search.trim()) body.search = queryParams.search.trim();

    if (queryParams?.groupName && queryParams.groupName.trim())
      body.groupName = queryParams.groupName.trim();

    if (queryParams?.teacherName && queryParams.teacherName.trim())
      body.teacherName = queryParams.teacherName.trim();

    if (queryParams?.gradeId !== undefined) body.gradeId = queryParams.gradeId;
    if (queryParams?.teacherId) body.teacherId = queryParams.teacherId;

    const groupIdsToSend = [
      ...(queryParams?.groupId ? [queryParams.groupId] : []),
      ...(queryParams?.groupIds || []),
    ].filter((id): id is string => id != null);
    if (groupIdsToSend.length > 0) body.groupIds = groupIdsToSend;
    if (queryParams?.teacherIds && queryParams.teacherIds.length > 0)
      body.teacherIds = queryParams.teacherIds;
    if (queryParams?.gradeIds && queryParams.gradeIds.length > 0)
      body.gradeIds = queryParams.gradeIds;

    if (queryParams?.classCode && queryParams.classCode.length > 0)
      body.classCode = queryParams.classCode;

    const requestKey = `${url}:${JSON.stringify(body)}`;
    const existing = classesReportInFlight.get(requestKey);
    if (existing) return existing;

    const promise = axiosInstance
      .post<GroupsReportResponse>(url, body)
      .then(res => res.data)
      .finally(() => {
        classesReportInFlight.delete(requestKey);
      });
    classesReportInFlight.set(requestKey, promise);
    return promise;
  },
  async createClass(
    organizationId: string,
    payload: { listOfTeachers: string[]; groupName: string; gradeId: string }
  ): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/groups`;
    return axiosInstance.post(url, payload);
  },
  async getClassById(schoolId: string, classId: string): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/groups/details/${classId}`;
    return axiosInstance.get(url);
  },
  async updateClass(
    schoolId: string,
    classId: string,
    payload: {
      groupName: string;
      gradeId: string;
      teacherIds: string[];
      expectedLastUpdateAt?: string;
    }
  ): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/groups/${classId}`;
    return axiosInstance.put(url, payload);
  },
  async deleteClass(schoolId: string, classId: string): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/groups/${classId}`;
    return axiosInstance.delete(url);
  },
};
